import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Pricing.css';

const API_PLANS_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '')}/payment/subscription-plans/`
  : null;

/** Supported plan currencies in Stripe: ILS, USD, RON, EUR. */
const PLAN_CURRENCIES = ['ILS', 'USD', 'RON', 'EUR'];
const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'SK', 'SI', 'ES', 'SE',
]);

function getPreferredPlanCurrencyForPlans(countryCode) {
  const code = (countryCode || '').toUpperCase().slice(0, 2);
  if (!code) return 'USD';
  if (code === 'IL') return 'ILS';
  if (code === 'RO') return 'RON';
  if (EU_COUNTRY_CODES.has(code)) return 'EUR';
  return 'USD';
}

/** Detect country code: try IP geo, then navigator.language. */
async function getCountryCodeForPricing() {
  try {
    const res = await fetch('https://ipapi.co/json/', { method: 'GET', headers: { Accept: 'application/json' } });
    if (res.ok) {
      const data = await res.json();
      const code = data.country_code || data.country;
      if (code) return code.toUpperCase().slice(0, 2);
    }
  } catch (e) {
    // ignore
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const parts = navigator.language.split('-');
    if (parts.length > 1) return parts[1].toUpperCase().slice(0, 2);
  }
  return null;
}

/** Canonical display order for LTR (Basic → Premium → Enterprise). RTL will reverse. */
const PLAN_ORDER = ['basic', 'premium', 'enterprise'];

function getPlanOrderIndex(planKey) {
  const i = PLAN_ORDER.indexOf(planKey || '');
  return i >= 0 ? i : PLAN_ORDER.length;
}

/** Sort plan groups by tier order; reverse for RTL. */
function sortPlansByTier(planGroups, isRTL) {
  const sorted = [...(planGroups || [])].sort((a, b) => {
    const keyA = getPlanKey(a.name);
    const keyB = getPlanKey(b.name);
    return getPlanOrderIndex(keyA) - getPlanOrderIndex(keyB);
  });
  return isRTL ? sorted.reverse() : sorted;
}

/** Map plan name (API/Stripe: "Premium"; any language) to canonical key for local name + features. */
const PLAN_NAME_TO_KEY = {
  basic: 'basic',
  premium: 'premium',
  enterprise: 'enterprise',
  בסיסי: 'basic',
  מקצועי: 'premium',
  פרימיום: 'premium',
  ארגוני: 'enterprise',
  أساسي: 'basic',
  احترافي: 'premium',
  بريميوم: 'premium',
  مؤسسي: 'enterprise',
  'базовый': 'basic',
  'профессиональный': 'premium',
  'премиум': 'premium',
  'корпоративный': 'enterprise',
};

function getPlanKey(apiName) {
  if (!apiName || typeof apiName !== 'string') return null;
  const trimmed = apiName.trim();
  const lower = trimmed.toLowerCase();
  return PLAN_NAME_TO_KEY[lower] || PLAN_NAME_TO_KEY[trimmed] || null;
}

/** Local feature keys per plan tier (all plans: support, unlimited appointments, notifications, calendar sync, reports). */
const PLAN_FEATURES = {
  basic: [
    'planFeatureSupport',
    'planFeatureUnlimitedAppointments',
    'planFeatureNotificationsToClients',
    'planFeatureGoogleCalendarSync',
    'planFeatureEarningReports',
    'planFeature1Employee',
  ],
  premium: [
    'planFeatureSupport',
    'planFeatureUnlimitedAppointments',
    'planFeatureNotificationsToClients',
    'planFeatureGoogleCalendarSync',
    'planFeatureEarningReports',
    'planFeatureUpTo5Employees',
    'planFeatureWhatsAppNotifications',
  ],
  enterprise: [
    'planFeatureSupport',
    'planFeatureUnlimitedAppointments',
    'planFeatureNotificationsToClients',
    'planFeatureGoogleCalendarSync',
    'planFeatureEarningReports',
    'planFeatureUnlimitedEmployees',
    'planFeatureWhatsAppNotifications',
  ],
};

function getLocalPlanContent(planKey, t) {
  const key = planKey && PLAN_FEATURES[planKey] ? planKey : null;
  const displayName = key ? t(`planName${key.charAt(0).toUpperCase() + key.slice(1)}`) : null;
  const featureKeys = key ? PLAN_FEATURES[key] : [];
  return { displayName, featureKeys };
}

/** Manual Enterprise plan: not from API/Stripe; show "Contact Us" instead of price. */
const MANUAL_ENTERPRISE_GROUP = {
  name: 'Enterprise',
  prices: [],
  isManualEnterprise: true,
};

/**
 * Group API plans by product name; each group has monthly and/or annual prices.
 * Excludes Enterprise from API and appends manual Enterprise (Contact Us).
 */
function groupPlansByProduct(plans) {
  const byName = {};
  (plans || []).forEach((plan) => {
    const name = (plan.name || 'Plan').trim() || 'Plan';
    if (!byName[name]) byName[name] = { name, prices: [] };
    byName[name].prices.push({
      id: plan.id,
      price: plan.price,
      currency: plan.currency || 'USD',
      currency_symbol: plan.currency_symbol || null,
      interval: (plan.interval || 'month').toLowerCase(),
      monthly_equivalent: plan.monthly_equivalent != null ? plan.monthly_equivalent : null,
    });
  });
  const groups = Object.values(byName).map((group) => ({
    ...group,
    prices: group.prices.sort((a, b) => (a.interval === 'year' ? 1 : 0) - (b.interval === 'year' ? 1 : 0)),
  }));
  // Remove Enterprise from API (if present) and add manual Enterprise
  const withoutEnterprise = groups.filter((g) => getPlanKey(g.name) !== 'enterprise');
  return [...withoutEnterprise, { ...MANUAL_ENTERPRISE_GROUP }];
}

/** Fallback symbols when backend does not send currency_symbol */
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', ILS: '₪', RON: 'lei', CAD: 'CA$', AUD: 'A$',
  CHF: 'CHF', RUB: '₽', JPY: '¥', INR: '₹',
};

function getSymbol(currency, currencySymbol) {
  return currencySymbol || CURRENCY_SYMBOLS[(currency || 'USD').toUpperCase()] || (currency || '') + ' ';
}

function formatPriceLine(amount, currency, interval, currencySymbol, t) {
  const symbol = getSymbol(currency, currencySymbol);
  const per = interval === 'year' ? t('perYear') : t('perMonth');
  return `${amount} ${symbol}/${per}`;
}

function formatPrice(price, currency, interval, currencySymbol, t) {
  const num = typeof price === 'string' ? parseFloat(price) : Number(price);
  const amount = Number.isFinite(num) ? num : 0;
  return formatPriceLine(amount, currency, interval, currencySymbol, t);
}

/** Monthly equivalent for annual price: from API (monthly_equivalent) or computed. */
function getMonthlyEquivalent(priceObj) {
  if (priceObj.interval !== 'year') return null;
  if (priceObj.monthly_equivalent != null) {
    const n = typeof priceObj.monthly_equivalent === 'string' ? parseFloat(priceObj.monthly_equivalent) : Number(priceObj.monthly_equivalent);
    if (Number.isFinite(n)) return n;
  }
  const annual = typeof priceObj.price === 'string' ? parseFloat(priceObj.price) : Number(priceObj.price);
  return Number.isFinite(annual) ? Math.round((annual / 12) * 100) / 100 : null;
}

/** Get the price for the selected interval, or fallback to the other interval if not available. */
function getPriceForInterval(group, interval) {
  const preferred = group.prices.find((p) => p.interval === interval);
  return preferred || group.prices[0] || null;
}

const Pricing = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(!!API_PLANS_URL);
  const [error, setError] = useState(null);
  const [interval, setInterval] = useState('month'); // 'month' | 'year'

  const isRTL = i18n.language === 'he' || i18n.language === 'ar';
  const sortedPlans = React.useMemo(
    () => sortPlansByTier(plans, isRTL),
    [plans, isRTL]
  );

  useEffect(() => {
    if (!API_PLANS_URL) {
      setError(t('pricingApiNotConfigured', 'Pricing is not configured.'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setError(null);
    (async () => {
      let currency = 'USD';
      try {
        const countryCode = await getCountryCodeForPricing();
        currency = getPreferredPlanCurrencyForPlans(countryCode);
      } catch (e) {
        // keep USD
      }
      if (cancelled) return;
      const url = `${API_PLANS_URL}${API_PLANS_URL.includes('?') ? '&' : '?'}currency=${encodeURIComponent(currency)}`;
      fetch(url, { method: 'GET', headers: { Accept: 'application/json' } })
        .then((res) => {
          if (!res.ok) throw new Error(res.statusText || 'Failed to load plans');
          return res.json();
        })
        .then((data) => {
          if (cancelled) return;
          setPlans(groupPlansByProduct(Array.isArray(data) ? data : []));
        })
        .catch((err) => {
          if (!cancelled) setError(err.message || t('pricingLoadError', 'Failed to load plans.'));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    })();
    return () => { cancelled = true; };
  }, [t]);

  const handleContactUs = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartNow = () => {
    const appUrl = process.env.REACT_APP_APP_URL || 'https://glowapp.foo-tech.net';
    if (appUrl) window.open(appUrl, '_blank');
  };

  if (loading) {
    return (
      <section id="pricing" className="pricing" aria-label={t('pricing')}>
        <div className="container">
          <h2 className="section-title">{t('pricing')}</h2>
          <p className="section-subtitle">{t('pricingDescription')}</p>
          <div className="pricing-loading" aria-busy="true">
            {t('loading', 'Loading...')}
          </div>
        </div>
      </section>
    );
  }

  if (error || plans.length === 0) {
    return (
      <section id="pricing" className="pricing" aria-label={t('pricing')}>
        <div className="container">
          <h2 className="section-title">{t('pricing')}</h2>
          <p className="section-subtitle">{t('pricingDescription')}</p>
          <p className="pricing-error">{error || t('pricingNoPlans', 'No plans available.')}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="pricing" aria-label={t('pricing')}>
      <div className="container">
        <h2 className="section-title">{t('pricing')}</h2>
        <p className="section-subtitle">{t('pricingDescription')}</p>

        <div className="pricing-toggle-wrap" role="group" aria-label={t('billingInterval', 'Billing interval')}>
          <button
            type="button"
            className={`pricing-toggle-btn ${interval === 'month' ? 'active' : ''}`}
            onClick={() => setInterval('month')}
            aria-pressed={interval === 'month'}
          >
            {t('monthly', 'Monthly')}
          </button>
          <button
            type="button"
            className={`pricing-toggle-btn ${interval === 'year' ? 'active' : ''}`}
            onClick={() => setInterval('year')}
            aria-pressed={interval === 'year'}
          >
            {t('annually', 'Annually')}
          </button>
        </div>

        <div className="pricing-grid">
          {sortedPlans.map((group, index) => {
            const planKey = getPlanKey(group.name);
            const price = getPriceForInterval(group, interval);
            const isContactUs = group.isManualEnterprise === true;
            if (!price && !isContactUs) return null;
            const local = getLocalPlanContent(planKey, t);
            const displayName = local.displayName || group.name;
            const isPopular = sortedPlans.length >= 2 && index === 1;
            return (
              <article key={group.name + index} className={`pricing-card ${isPopular ? 'popular' : ''}`}>
                {isPopular && <div className="popular-badge" aria-label={t('mostPopular')}>{t('mostPopular')}</div>}
                <h3 className="plan-name">{displayName}</h3>
                {local.featureKeys.length > 0 && (
                  <ul className="plan-features">
                    {local.featureKeys.map((fk) => (
                      <li key={fk}>
                        <span className="check-icon" aria-hidden="true">✓</span>
                        {t(fk)}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="plan-price-block">
                  {isContactUs ? (
                    <div className="plan-price plan-price--primary" aria-label={t('customPrice')}>
                      {t('customPrice')}
                    </div>
                  ) : price.interval === 'year' && getMonthlyEquivalent(price) != null ? (
                    <>
                      <div className="plan-price plan-price--primary">
                        {formatPriceLine(
                          getMonthlyEquivalent(price),
                          price.currency,
                          'month',
                          price.currency_symbol,
                          t
                        )}
                      </div>
                      <div className="plan-price plan-price--secondary" aria-label={formatPrice(price.price, price.currency, 'year', price.currency_symbol, t)}>
                        {formatPrice(price.price, price.currency, 'year', price.currency_symbol, t)}
                      </div>
                    </>
                  ) : (
                    <div className="plan-price" aria-label={formatPrice(price.price, price.currency, price.interval, price.currency_symbol, t)}>
                      {formatPrice(price.price, price.currency, price.interval, price.currency_symbol, t)}
                    </div>
                  )}
                </div>
                {isContactUs ? (
                  <button onClick={handleContactUs} className="btn btn-primary plan-cta" aria-label={`${t('contactUs')} - ${displayName}`}>
                    {t('contactUs')}
                  </button>
                ) : (
                  <button onClick={handleStartNow} className="btn btn-primary plan-cta" aria-label={`${t('startNow')} - ${displayName}`}>
                    {t('startNow')}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
